import React from 'react';
import { useParams } from '@tanstack/react-router';
import { LoadingOrbit, Markdown, PillToggle, Text } from '@chatsdet/ui';
import { usePublicGist } from '../../features/utilities/api';

/**
 * Public, unauthenticated gist viewer at `/u/g/$id`. Only resolves gists with
 * visibility='public' that haven't expired (enforced by the public API route).
 * Rendered outside AppShell (see AppShell's `/u/g/` bypass).
 */
export function PublicGist() {
  const { id } = useParams({ from: '/u/g/$id' });
  const { data, isLoading, isError } = usePublicGist(id);
  const gist = data?.data;
  const [mode, setMode] = React.useState<'rendered' | 'plain'>('rendered');

  React.useEffect(() => {
    if (gist) setMode(gist.format === 'markdown' ? 'rendered' : 'plain');
  }, [gist]);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-10">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <LoadingOrbit />
          </div>
        ) : isError || !gist ? (
          <div className="text-center py-20">
            <Text weight="bold" className="text-xl">
              Gist not found
            </Text>
            <Text className="text-muted-foreground">
              This gist is private, expired, or does not exist.
            </Text>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Text weight="bold" className="text-2xl">
                  {gist.title}
                </Text>
                <Text className="text-muted-foreground text-sm">
                  {new Date(gist.createdAt).toLocaleString()}
                </Text>
              </div>
              <PillToggle
                value={mode}
                onChange={(v) => setMode(v as 'rendered' | 'plain')}
                options={[
                  { value: 'rendered', label: 'Markdown' },
                  { value: 'plain', label: 'Plain' },
                ]}
              />
            </div>
            {mode === 'rendered' ? (
              <Markdown className="prose prose-sm max-w-none text-foreground">
                {gist.content}
              </Markdown>
            ) : (
              <pre className="whitespace-pre-wrap break-words text-sm font-mono">{gist.content}</pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
